/**
 * @file Lays one answered stored report query out for the renderers: a single
 * row as label and value lines ("Booked $2,284,000"), several rows as a table,
 * and `quoteValueTotal` as one line per stage list then their exact sum
 * ("Quoted (Quote + Buy) $Z"), added here at render time because no server row
 * crosses stages. Consumers: the React run view and the PDF, so both lay a run
 * out alike. Headers and cells come from the registry, the stored columns and
 * the frozen names alone; a registry column the run lacks is skipped. It takes
 * the answered entry read-only, so a Valtio snapshot passes as is.
 */
import type { NamedQueryCell, NamedQueryId, NamedQueryRefusedColumn } from './Analytics.NamedQuery.types';
import type { AnalyticsReportRunNames } from './Analytics.Report.types';
import type { AnalyticsReportLine, AnalyticsReportQueryView } from './AnalyticsReportQueryView.types';
import { analyticsColumnPresentation } from './analyticsColumnPresentation';
import { analyticsColumnText } from './analyticsColumnText';
import { analyticsCurrencyText } from './analyticsCurrencyText';
import { analyticsDecimalFraction } from './analyticsDecimalFraction';
import { analyticsDecimalSum } from './analyticsDecimalSum';

type AnsweredQueryRun = {
  query: NamedQueryId;
  columns: readonly string[];
  rows: readonly Readonly<Record<string, NamedQueryCell>>[];
  refusedColumns: readonly NamedQueryRefusedColumn[];
};

const PROJECTION_NOTE = 'projected across stages';
const NAMES_NOTE = 'Names were not recorded for this run';
const STAGES = 'stages';
const AMOUNT = 'quotedAmount';

const refusalOf = (entry: AnsweredQueryRun, column: string) =>
  entry.refusedColumns.find((refused) => refused.column === column) ?? null;

const namesNote = (entry: AnsweredQueryRun, column: string, names: AnalyticsReportRunNames | undefined): string | null => {
  const { format } = analyticsColumnPresentation(entry.query, column);
  return names === undefined && (format === 'person' || format === 'team') ? NAMES_NOTE : null;
};

const quotedLines = (entry: AnsweredQueryRun, names: AnalyticsReportRunNames | undefined, title: string): AnalyticsReportLine[] => {
  const refused = refusalOf(entry, AMOUNT);
  const stages = entry.rows.map((row) => analyticsColumnText(entry.query, STAGES, row[STAGES], names) ?? '—');
  const amounts = entry.rows.map((row) => String(row[AMOUNT] ?? ''));
  const exact = amounts.every((amount) => analyticsDecimalFraction(amount) !== undefined);
  const stageLines = entry.rows.map((row, index) => ({
    label: `${title} — ${stages[index]}`,
    text: analyticsColumnText(entry.query, AMOUNT, row[AMOUNT], names),
    refused,
    note: null,
  }));
  return [
    ...stageLines,
    {
      label: `${title} (${stages.join(' + ')})`,
      text: exact ? analyticsCurrencyText(analyticsDecimalSum(amounts)) : null,
      refused,
      note: PROJECTION_NOTE,
    },
  ];
};

export const analyticsReportQueryView = (
  entry: AnsweredQueryRun,
  names: AnalyticsReportRunNames | undefined,
  title: string,
): AnalyticsReportQueryView => {
  if (entry.query === 'quoteValueTotal') return { kind: 'lines', lines: quotedLines(entry, names, title) };
  const columns = entry.columns.map((column) => ({
    label: analyticsColumnPresentation(entry.query, column).label,
    refused: refusalOf(entry, column),
    note: namesNote(entry, column, names),
  }));
  const rows = entry.rows.map((row) => entry.columns.map((column) => analyticsColumnText(entry.query, column, row[column], names)));
  if (rows.length !== 1) return { kind: 'table', columns, rows };
  return { kind: 'lines', lines: columns.map((column, index) => ({ ...column, text: rows[0][index] })) };
};
